export const integrations = {
  title: 'Integrations',
  subtitle: 'Connect your tools and services to enhance your workflow.',

  categories: {
    cms: 'CMS',
    analytics: 'Google & analytics',
    social: 'Social networks',
  },

  providers: {
    webflow: {
      name: 'Webflow',
      description: 'Publish posts directly to your Webflow CMS.',
    },
    wordpress: {
      name: 'WordPress',
      description: 'Publish posts, pages, and landing assets to your WordPress site.',
    },
    wordpress_com: {
      name: 'WordPress.com',
      description: 'Publish posts to your WordPress.com site (coming soon).',
    },
    google_search_console: {
      name: 'Google Search Console',
      description: 'Connect your Search Console to analyze search performance and indexing.',
    },
    google_analytics_4: {
      name: 'Google Analytics 4',
      description: 'Connect GA4 to measure traffic from AI assistants and conversational engines.',
    },
    framer: {
      name: 'Framer',
      description: 'Publish content directly to your Framer CMS collections.',
    },
    tiendanube: {
      name: 'Tiendanube',
      description: 'Publish blog posts directly to your Tiendanube / Nuvemshop store.',
    },
  },

  status: {
    connected: 'Connected',
    expired: 'Expired — reconnect required',
    revoked: 'Revoked — reconnect required',
    error: 'Error',
    pendingSiteSelection: 'Select a site',
    notConnected: 'Not connected',
    comingSoon: 'Coming soon',
  },

  actions: {
    connect: 'Connect',
    connecting: 'Redirecting…',
    disconnect: 'Disconnect',
    disconnecting: 'Disconnecting…',
    reconnect: 'Reconnect',
    selectSite: 'Select site',
    selectProperty: 'Select property',
    selectCollection: 'Select collection',
  },

  siteLabel: 'Site',
  connectedAtLabel: 'Connected on',
  tiendanubeStoreLabel: 'Store {{storeId}}',

  permissions: {
    adminOnly: 'Only organization owners and admins can manage integrations.',
  },

  siteSelectionDialog: {
    title: 'Select a site',
    description: 'Your Google Search Console account has multiple properties. Choose which one to connect.',
    confirm: 'Connect site',
    cancel: 'Cancel',
    searchPlaceholder: 'Search properties...',
    noResults: 'No properties match your search.',
    domainProperty: 'Domain property',
    urlProperty: 'URL property',
  },

  ga4PropertySelectionDialog: {
    title: 'Select a GA4 property',
    description: 'Your Google account has access to multiple Analytics properties. Choose the one Sergeo should read.',
    confirm: 'Connect property',
    cancel: 'Cancel',
    searchPlaceholder: 'Search properties...',
    noResults: 'No properties match your search.',
    propertyIdLabel: 'ID {{id}}',
  },

  framerConnectDialog: {
    title: 'Connect Framer',
    description:
      'Paste your Framer API key and the project URL. You can find your API key in your Framer account settings.',
    apiKeyLabel: 'API key',
    apiKeyPlaceholder: 'Paste your Framer API key',
    projectUrlLabel: 'Project URL',
    projectUrlPlaceholder: 'https://framer.com/projects/Sites--xxxx',
    connect: 'Connect',
    connecting: 'Connecting…',
    cancel: 'Cancel',
    showApiKey: 'Show API key',
    hideApiKey: 'Hide API key',
    validation: {
      apiKeyRequired: 'API key is required.',
      projectUrlInvalid: 'Must be a Framer project URL (https://framer.com/projects/...).',
    },
    errors: {
      invalidApiKey: 'The API key is invalid or expired. Please check and try again.',
      invalidProjectUrl: "We couldn't reach that Framer project. Make sure the URL is correct.",
      noCollections: 'This Framer project has no CMS collections yet.',
      connectFailed: 'Connection to Framer failed. Please try again in a moment.',
      alreadyConnected: 'This Framer project is already connected.',
      generic: 'Something went wrong. Please try again.',
    },
  },

  webflowConnectDialog: {
    title: 'Connect Webflow',
    description:
      'Connect your Webflow site using a Site API token. Works with site-level admin access — no workspace admin required.',
    apiTokenLabel: 'Site API token',
    apiTokenPlaceholder: 'Paste your Webflow Site API token',
    helpText:
      'Generate the token in Webflow under Site settings → Apps & integrations → API access, with CMS and Sites scopes set to read and write.',
    connect: 'Connect',
    connecting: 'Connecting…',
    cancel: 'Cancel',
    showApiToken: 'Show API token',
    hideApiToken: 'Hide API token',
    useOAuthInstead: 'Prefer to sign in with Webflow instead? Connect via OAuth',
    validation: {
      apiTokenRequired: 'API token is required.',
    },
    errors: {
      invalidToken:
        'We could not read the site and its CMS collections with this token. Check it is valid and was created with CMS and Sites access.',
      multipleSites:
        'This token has access to multiple sites. Generate a Site API token from the site settings instead.',
      noSites: 'This token has no site available. Check it was generated on the right site.',
      alreadyConnected: 'A Webflow site is already connected for this organization.',
      generic: 'Something went wrong. Please try again.',
    },
  },

  wordpressConnectDialog: {
    title: 'Connect WordPress',
    description:
      'Connect a self-hosted WordPress site using an application password. The site must expose the WordPress REST API over HTTPS.',
    siteUrlLabel: 'Site URL',
    siteUrlPlaceholder: 'https://example.com',
    usernameLabel: 'Username',
    usernamePlaceholder: 'WordPress username',
    applicationPasswordLabel: 'Application password',
    applicationPasswordPlaceholder: 'Paste your WordPress application password',
    helpText:
      'Create an application password in WordPress under Users → Profile → Application Passwords.',
    connect: 'Connect',
    connecting: 'Connecting…',
    cancel: 'Cancel',
    showApplicationPassword: 'Show application password',
    hideApplicationPassword: 'Hide application password',
    validation: {
      siteUrlInvalid: 'Enter a valid WordPress site URL.',
      siteUrlHttps: 'WordPress site URL must use HTTPS.',
      usernameRequired: 'Username is required.',
      applicationPasswordRequired: 'Application password is required.',
    },
    errors: {
      invalidSiteUrl: 'Enter a valid HTTPS WordPress site URL.',
      restUnavailable: 'We could not reach the WordPress REST API on this site.',
      invalidCredentials: 'The username or application password is invalid.',
      routesUnavailable:
        'This site does not expose the required posts, pages, and media REST routes.',
      alreadyConnected: 'This WordPress site is already connected.',
      generic: 'Something went wrong. Please try again.',
    },
  },

  tiendanubeConnectDialog: {
    title: 'Connect Tiendanube',
    description:
      'Connect your Tiendanube / Nuvemshop store using its store ID and an access token.',
    storeIdLabel: 'Store ID',
    storeIdPlaceholder: '123456',
    accessTokenLabel: 'Access token',
    accessTokenPlaceholder: 'Paste your Tiendanube access token',
    authorNameLabel: 'Author name (optional)',
    authorNamePlaceholder: 'e.g. your brand name',
    authorNameHint:
      'Shown publicly as the author signature on the blog posts published to your store.',
    helpText:
      'Find your store ID and generate an access token from your Tiendanube admin under Applications → Develop your own app.',
    connect: 'Connect',
    connecting: 'Connecting…',
    cancel: 'Cancel',
    showAccessToken: 'Show access token',
    hideAccessToken: 'Hide access token',
    validation: {
      storeIdRequired: 'Store ID is required.',
      storeIdNumeric: 'Store ID must contain only numbers.',
      accessTokenRequired: 'Access token is required.',
      authorNameTooLong: 'Author name cannot exceed 80 characters.',
    },
    errors: {
      apiUnavailable: 'We could not reach the Tiendanube API for this store.',
      invalidCredentials: 'The store ID or access token is invalid.',
      alreadyConnected: 'This Tiendanube store is already connected.',
      generic: 'Something went wrong. Please try again.',
    },
  },

  framerCollectionDialog: {
    title: 'Select a collection',
    description:
      'Choose the Framer CMS collection where Sergeo should publish new content.',
    confirm: 'Connect collection',
    cancel: 'Cancel',
    searchPlaceholder: 'Search collections...',
    noResults: 'No collections match your search.',
    emptyCollections:
      'No CMS collections found in this project. Add a collection in Framer and try again.',
    readOnly: 'Read only',
    managedBy: 'Managed by {{manager}}',
    slugField: 'Slug: {{field}}',
    noSlugField: 'No slug field configured',
  },

  toast: {
    connected: 'Integration connected successfully.',
    disconnected: 'Integration disconnected.',
    connectFailed: 'We could not start the connection flow. Please try again.',
    disconnectFailed: 'We could not disconnect the integration. Please try again.',
    connectionError: 'The connection could not be completed: {{error}}',
    siteSelectionFailed: 'Could not select the site. Please try again.',
    propertySelectionFailed: 'Could not select the property. Please try again.',
    framerConnectFailed: 'Could not connect Framer. Check your API key and project URL.',
    wordpressConnectFailed: 'Could not connect WordPress. Check your site and credentials.',
    tiendanubeConnectFailed: 'Could not connect Tiendanube. Check your store ID and access token.',
    collectionSelectionFailed: 'Could not select the collection. Please try again.',
  },

  disconnectDialog: {
    title: 'Disconnect {{provider}}?',
    description:
      'Sergeo will lose access to publish content to this provider. You can reconnect at any time.',
    confirm: 'Disconnect',
    cancel: 'Cancel',
  },

  empty: {
    title: 'No integrations available',
    subtitle: 'Integrations will appear here once they are enabled for your plan.',
  },

  errorState: {
    title: 'Something went wrong',
    subtitle: 'We could not load your integrations. Please try again.',
    retry: 'Retry',
  },

  publishShortcut: {
    label: 'Publish with Sergeo',
    labelWithProvider: 'Publish to {{provider}} with Sergeo',
    prefillMessage: 'Publish the content "{{contentTitle}}"{{siteSuffix}} to {{providerName}}. referentContentId={{contentId}}',
    ariaLabel: 'Publish to {{provider}} with Sergeo',
  },
}
